import { useEffect } from 'react';
import { LazyDashboards, LazyPages, LazyTestPages } from '../routes/LazyRoutes';
import { performanceMonitor } from './performanceMonitor';

/**
 * Route-based preloading system for optimizing navigation performance
 */

interface RouteConfig {
  path: string;
  component: React.LazyExoticComponent<any>;
  preloadTriggers: PreloadTrigger[];
  priority: 'low' | 'medium' | 'high';
  dependencies?: string[];
}

type PreloadTrigger = 'hover' | 'visible' | 'idle' | 'immediate' | 'user-intent';

class RoutePreloader {
  private routes = new Map<string, RouteConfig>();
  private preloadedRoutes = new Set<string>();
  private preloadingRoutes = new Set<string>();
  private linkObserver?: IntersectionObserver;
  private idleCallback?: number;

  constructor() {
    this.initializeRoutes();
    this.setupLinkObserver();
    this.setupIdlePreloading();
  }

  private initializeRoutes(): void {
    // Main application routes
    this.registerRoute({
      path: '/',
      component: LazyDashboards.OpsRecruiterDashboard,
      preloadTriggers: ['immediate'],
      priority: 'high'
    });

    this.registerRoute({
      path: '/dashboard',
      component: LazyDashboards.OpsRecruiterDashboard,
      preloadTriggers: ['immediate'],
      priority: 'high'
    });

    this.registerRoute({
      path: '/candidates',
      component: LazyPages.CandidateDiscovery,
      preloadTriggers: ['hover', 'user-intent'],
      priority: 'high'
    });

    this.registerRoute({
      path: '/jobs',
      component: LazyPages.JobPosting,
      preloadTriggers: ['hover', 'user-intent'],
      priority: 'high'
    });

    this.registerRoute({
      path: '/users',
      component: LazyPages.UserManagement,
      preloadTriggers: ['hover'],
      priority: 'medium'
    });

    this.registerRoute({
      path: '/agents',
      component: LazyPages.AgentTestPage,
      preloadTriggers: ['hover'],
      priority: 'medium'
    });

    this.registerRoute({
      path: '/search',
      component: LazyPages.SearchTestPage,
      preloadTriggers: ['hover'],
      priority: 'medium'
    });

    // Test routes
    this.registerRoute({
      path: '/test',
      component: LazyTestPages.TestPageNavigation,
      preloadTriggers: ['hover'],
      priority: 'low'
    });

    this.registerRoute({
      path: '/test/design-system',
      component: LazyTestPages.DesignSystemTestPage,
      preloadTriggers: ['hover'],
      priority: 'low'
    });

    this.registerRoute({
      path: '/test/dashboard',
      component: LazyTestPages.DashboardTestPage,
      preloadTriggers: ['hover'],
      priority: 'low'
    });

    this.registerRoute({
      path: '/test/agents',
      component: LazyTestPages.AgentInteractionTestPage,
      preloadTriggers: ['hover'],
      priority: 'low'
    });

    this.registerRoute({
      path: '/test/search',
      component: LazyTestPages.SearchSystemTestPage,
      preloadTriggers: ['hover'],
      priority: 'low'
    });

    this.registerRoute({
      path: '/test/communication',
      component: LazyTestPages.CommunicationTestPage,
      preloadTriggers: ['hover'],
      priority: 'low'
    });

    this.registerRoute({
      path: '/test/touch',
      component: LazyTestPages.TouchInteractionTestPage,
      preloadTriggers: ['hover'],
      priority: 'low'
    });

    this.registerRoute({
      path: '/test/device',
      component: LazyTestPages.DeviceIntegrationTestPage,
      preloadTriggers: ['hover'],
      priority: 'low'
    });
  }

  private registerRoute(config: RouteConfig): void {
    this.routes.set(config.path, config);
  }

  private setupLinkObserver(): void {
    if ('IntersectionObserver' in window) {
      this.linkObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const link = entry.target as HTMLAnchorElement;
              const href = link.getAttribute('href');
              if (href) {
                this.preloadRoute(href, 'visible');
              }
            }
          });
        },
        { rootMargin: '50px' }
      );
    }
  }

  private setupIdlePreloading(): void {
    if ('requestIdleCallback' in window) {
      this.idleCallback = requestIdleCallback(() => {
        this.preloadIdleRoutes();
      });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => {
        this.preloadIdleRoutes();
      }, 2000);
    }
  }

  /**
   * Preload a route based on trigger
   */
  async preloadRoute(path: string, trigger: PreloadTrigger): Promise<void> {
    const route = this.routes.get(path);
    if (!route || this.preloadedRoutes.has(path) || this.preloadingRoutes.has(path)) {
      return;
    }

    // Check if this trigger is configured for this route
    if (!route.preloadTriggers.includes(trigger)) {
      return;
    }

    this.preloadingRoutes.add(path);
    const startTime = Date.now();

    try {
      // Preload dependencies first
      if (route.dependencies) {
        await Promise.all(
          route.dependencies.map(dep => this.preloadRoute(dep, trigger))
        );
      }

      // Preload the route component
      try {
        const payload = (route.component as any)._payload;
        if (payload && payload._result) {
          await payload._result;
        } else {
          // Fallback: trigger the lazy loading
          await (route.component as any)._init((route.component as any)._payload);
        }
      } catch (error) {
        // Final fallback: the component will be loaded when actually needed
        console.warn('Could not preload route component, will load on demand');
      }
      
      this.preloadedRoutes.add(path);
      const loadTime = Date.now() - startTime;
      
      performanceMonitor.recordMetric(`route-preload-${path}`, loadTime, {
        trigger,
        priority: route.priority
      });

      if (process.env.NODE_ENV === 'development') {
        console.log(`Preloaded route ${path} in ${loadTime}ms (trigger: ${trigger})`);
      }
    } catch (error) {
      console.warn(`Failed to preload route ${path}:`, error);
    } finally {
      this.preloadingRoutes.delete(path);
    }
  }

  /**
   * Preload routes during idle time
   */
  private async preloadIdleRoutes(): Promise<void> {
    const idleRoutes = Array.from(this.routes.entries())
      .filter(([path, route]) => 
        route.preloadTriggers.includes('idle') && 
        !this.preloadedRoutes.has(path)
      )
      .sort(([, a], [, b]) => {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      });

    for (const [path] of idleRoutes) {
      await this.preloadRoute(path, 'idle');
      // Add delay between preloads to avoid blocking
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  /**
   * Setup hover preloading for navigation links
   */
  setupHoverPreloading(): void {
    document.addEventListener('mouseover', (event) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement;
      
      if (link && link.href) {
        const path = new URL(link.href).pathname;
        this.preloadRoute(path, 'hover');
      }
    });

    // Also setup for focus events (keyboard navigation)
    document.addEventListener('focusin', (event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'A' && target.getAttribute('href')) {
        const path = new URL((target as HTMLAnchorElement).href).pathname;
        this.preloadRoute(path, 'hover');
      }
    });
  }

  /**
   * Setup user intent prediction
   */
  setupUserIntentPreloading(): void {
    let mouseDirection = { x: 0, y: 0 };
    let lastMousePosition = { x: 0, y: 0 };

    const isMovingTowards = (event: MouseEvent, rect: DOMRect): boolean => {
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const toCenter = {
        x: centerX - event.clientX,
        y: centerY - event.clientY
      };

      // Check if mouse direction is generally towards the element
      const dotProduct = mouseDirection.x * toCenter.x + mouseDirection.y * toCenter.y;
      return dotProduct > 0;
    };

    document.addEventListener('mousemove', (event) => {
      mouseDirection = {
        x: event.clientX - lastMousePosition.x,
        y: event.clientY - lastMousePosition.y
      };
      lastMousePosition = { x: event.clientX, y: event.clientY };

      // Predict user intent based on mouse movement towards links
      const links = document.querySelectorAll('a[href]');
      links.forEach((link) => {
        const rect = link.getBoundingClientRect();
        const distance = Math.sqrt(
          Math.pow(event.clientX - (rect.left + rect.width / 2), 2) +
          Math.pow(event.clientY - (rect.top + rect.height / 2), 2)
        );

        // If mouse is moving towards the link and is close
        if (distance < 100 && isMovingTowards(event, rect)) {
          const path = new URL((link as HTMLAnchorElement).href).pathname;
          this.preloadRoute(path, 'user-intent');
        }
      });
    });
  }

  /**
   * Observe links for visibility-based preloading
   */
  observeLinks(): void {
    if (!this.linkObserver) return;

    const links = document.querySelectorAll('a[href]');
    links.forEach(link => {
      this.linkObserver!.observe(link);
    });
  }

  /**
   * Get preloading statistics
   */
  getStats(): {
    totalRoutes: number;
    preloadedRoutes: number;
    preloadingRoutes: number;
    preloadedPaths: string[];
  } {
    return {
      totalRoutes: this.routes.size,
      preloadedRoutes: this.preloadedRoutes.size,
      preloadingRoutes: this.preloadingRoutes.size,
      preloadedPaths: Array.from(this.preloadedRoutes)
    };
  }

  /**
   * Cleanup observers and callbacks
   */
  cleanup(): void {
    this.linkObserver?.disconnect();
    if (this.idleCallback) {
      cancelIdleCallback(this.idleCallback);
    }
  }
}

// Global route preloader instance
export const routePreloader = new RoutePreloader();

/**
 * Hook for setting up route preloading in components
 */
export function useRoutePreloading() {
  useEffect(() => {
    routePreloader.setupHoverPreloading();
    routePreloader.setupUserIntentPreloading();
    routePreloader.observeLinks();

    return () => {
      routePreloader.cleanup();
    };
  }, []);

  return {
    preloadRoute: (path: string, trigger: PreloadTrigger = 'hover') => 
      routePreloader.preloadRoute(path, trigger),
    getStats: () => routePreloader.getStats()
  };
}